import { Component } from '@theme/component';
import { redirectAddToCartToSizeDrawer } from '@theme/utilities';

class StickyAddToCart extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    // Delegated from the host, not bound on the button: a color swap morphs the whole section and
    // may replace the button node (cf. product-color-swap.js).
    this.addEventListener('click', this.#handleAddToCartClick, {
      signal: this.#abortController.signal,
      capture: true,
    });

    this.#setupObserver();
  }

  /**
   * Re-rendered by the Section Rendering API (color swap): `.product__buy` is a fresh node, so the
   * observer would keep watching the detached one.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#setupObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Visible, on mobile and desktop alike, once the real add-to-cart CTA has scrolled above the
   * viewport: the bar duplicates that CTA, so it is only useful while the original is out of reach.
   *
   * Deliberately watches the CTA row and not the whole `.product-details` column: on desktop that
   * column is `position: sticky` and stays pinned far longer, so "all of it cleared the viewport
   * top" is only true in the last few dozen pixels of available scroll on a tall window — measured
   * at 1600x1300, that left an 85px window out of 1419px of scroll, i.e. never in practice.
   */
  #setupObserver() {
    this.#observer?.disconnect();

    /** @type {HTMLElement | null} */
    const cta = this.closest('.shopify-section')?.querySelector('.product__buy') ?? null;
    if (!cta) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          this.hidden = true;
          return;
        }
        const scrolledPast = entry.boundingClientRect.bottom < 0;
        this.hidden = entry.isIntersecting || !scrolledPast;
      },
      { threshold: 0 },
    );

    this.#observer.observe(cta);
  }

  /**
   * Same rule as the buy-box CTA: no explicit size choice yet → open the size drawer instead of
   * submitting the form with the server-resolved default variant, and let the size chosen there
   * complete this add.
   * @param {MouseEvent} event
   */
  #handleAddToCartClick = (event) => {
    const { target } = event;
    if (!(target instanceof Element) || !target.closest('[ref="addToCartButton"]')) return;

    if (redirectAddToCartToSizeDrawer(this)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
